import { ConfirmDialog } from 'components/confirm-dialog/confirm-dialog'
import {
	DotMatrix,
	AppButton,
	Avatar,
	Badge,
	DEFAULT_COUNTRIES,
	Input,
	PageHeader,
	PhoneInput,
	SearchableSelect,
	StatCard,
	Tooltip
} from '@clickguard/design-system'
import { useStores } from 'hooks'
import { ChangeEvent, useState } from 'react'
import { toast } from 'react-toastify'
import { useUiStore } from 'store'
import timezones from 'timezones-list'
import { parsePhoneNumber } from 'awesome-phonenumber'
import { EmailAddresInfoDTO } from '../../../../../api-models'
import { CountriesList } from '../../../config/countries'
import { Download01, RefreshCw01 } from '../../../components/icons'

// Figma badge: bg success-50, 1px success border, success text, 12/16 semibold, r-16.
const BADGE_BASE =
	'!rounded-[16px] !border !px-1.5 !py-0.5 !text-[12px] !leading-4 !font-semibold'
const VERIFIED_BADGE = '!bg-[color:var(--badge-green-bg,#edfcf3)] !border-[var(--badge-green-border,#17b26a)] !text-[color:var(--badge-green-ink,#17b26a)]'
const UNVERIFIED_BADGE = '!bg-[color:var(--badge-amber-bg,#fffaeb)] !border-[var(--badge-amber-border,#f79009)] !text-[color:var(--badge-amber-ink,#f79009)]'

const dialDigitsFor = (iso: string) =>
	(DEFAULT_COUNTRIES.find((c) => c.code === iso)?.dial || '').replace('+', '')

export const ProfileSetting = () => {
	const { userStore } = useStores()
	const { user, updateProfile, updatePhoto, addNewEmail } = userStore
	const isLoading = useUiStore((s) => s.isLoading)

	if (!user) {
		return (
			<div className="flex justify-center py-16">
				<DotMatrix size="lg" />
			</div>
		)
	}

	const [userInfoState, setUserInfoState] = useState({
		fullName: user.name || '',
		timeZone: user.timezone || '',
		country: user.country || ''
	})
	const [phoneCountry, setPhoneCountry] = useState<string>(() => {
		const pn = user.phone_number ? parsePhoneNumber(user.phone_number) : undefined
		return pn?.regionCode || 'US'
	})
	const [phoneLocal, setPhoneLocal] = useState<string>(() => {
		const pn = user.phone_number ? parsePhoneNumber(user.phone_number) : undefined
		return pn?.number?.significant || ''
	})
	const [userInfoErrors, SetUserInfoErrors] = useState({
		fullName: '',
		timeZone: '',
		phone: '',
		country: ''
	})

	const tzList = timezones.map((item) => item.tzCode)
	const countriesList = Object.values(CountriesList).map((item) => item.name)
	const [file, setFile] = useState<File | null>(null)
	const [filePreview, setFilePreview] = useState<string | undefined>(undefined)

	const phoneDigits = `${dialDigitsFor(phoneCountry)}${phoneLocal.replace(
		/\D/g,
		''
	)}`

	const handleChange = (value: string, name: string) => {
		setUserInfoState({ ...userInfoState, [name]: value })
	}

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			const selectedFile = e.target.files[0]
			if (selectedFile) {
				const selectedFilePreview = URL.createObjectURL(e.target.files[0])
				setFile(selectedFile)
				setFilePreview(selectedFilePreview)
			}
		}
	}

	const handleSave = async () => {
		if (!handleValidation()) return

		await updateProfile({
			name: userInfoState.fullName,
			phone_number: `+${phoneDigits}`,
			timezone: userInfoState.timeZone,
			country: userInfoState.country
		})
		if (file) {
			const formData = new FormData()
			formData.append('avatar', file, file.name)
			await updatePhoto(formData)
		}
	}

	const handleValidation = () => {
		const tempError = { fullName: '', phone: '', timeZone: '', country: '' }
		if (userInfoState.fullName.trim().length <= 1) {
			tempError.fullName = 'Please enter a valid name'
		}
		if (phoneLocal.trim().length <= 1 || !parsePhoneNumber(`+${phoneDigits}`).valid) {
			tempError.phone = 'Please enter a valid phone number'
		}
		if (userInfoState.timeZone.trim().length <= 1) {
			tempError.timeZone = 'Please select a valid time zone'
		}
		if (userInfoState.country.trim().length <= 1) {
			tempError.country = 'Please select a valid Country'
		}

		SetUserInfoErrors({ ...tempError })
		return Object.values(tempError).every((x) => x == '')
	}

	const [newEmail, setNewEmail] = useState(false)
	const [emailValue, setEmailValue] = useState<string>('')

	const handleAddnewEmail = async () => {
		const emailExists = user.email_addresses.find(
			(email) => email.email === emailValue.trim()
		)
		if (emailExists) {
			toast.error(
				`This email has already been ${
					emailExists.email_verified ? 'verified' : ' invited'
				}`
			)
			return
		}
		try {
			await addNewEmail({ email: emailValue })
			setEmailValue('')
			setNewEmail(false)
		} catch (error) {
			return
		}
	}

	return (
		<div className="w-full">
			<PageHeader
				layout="stacked"
				title="Profile"
				subtitle="Manage your personal profile information and email addresses"
			/>
			<div className="flex flex-col gap-4 pt-4">
				<StatCard
					size="regular"
					divider={false}
					className="w-full !p-4 !gap-4"
					title={
						<span className="text-[18px] leading-6 font-medium text-t-base-blue">
							Profile Information
						</span>
					}
					trailing={
						<AppButton
							loader="dots"
							variant="primary"
							size="md"
							label="Save"
							onClick={() => handleSave()}
							isLoading={isLoading('USER_SAVE_PROFILE')}
						/>
					}
				>
					<div className="w-[calc(50%-8px)]">
						<Input
							label="Full Name"
							size="md"
							state={userInfoErrors.fullName ? 'error' : 'default'}
							error={userInfoErrors.fullName || undefined}
							value={userInfoState.fullName}
							onChange={(e) => handleChange(e.target.value, 'fullName')}
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<p className="text-[14px] leading-[18px] font-medium text-t-base-blue">
							Profile picture
						</p>
						<div className="flex w-fit items-center gap-3 rounded-xl border border-t-checkbox p-[13px]">
							<Avatar
								size="sm"
								src={filePreview ?? user.avatar ?? undefined}
								name={user.name || undefined}
								alt="Profile picture"
							/>
							<input
								className="hidden"
								id="photo"
								name="photo"
								type="file"
								onChange={(e) => handleFileChange(e)}
							/>
							<label className="cursor-pointer" htmlFor="photo">
								<AppButton
									variant="secondary"
									size="md"
									label="Change"
									className="!pointer-events-none !border-t-card-border !font-semibold !text-t-base-blue"
								/>
							</label>
						</div>
					</div>

					<div className="w-[calc(50%-8px)]">
						<PhoneInput
							label="Phone number"
							size="md"
							state={userInfoErrors.phone ? 'error' : 'default'}
							error={userInfoErrors.phone || undefined}
							value={phoneLocal}
							onChange={(v) => setPhoneLocal(v)}
							country={phoneCountry}
							onCountryChange={(c) => setPhoneCountry(c.code)}
						/>
					</div>

					<div className="flex w-full gap-4">
						<div className="flex w-full flex-col gap-1.5">
							<p className="text-[14px] leading-[18px] font-medium text-[color:var(--text-primary)]">
								Country
							</p>
							<SearchableSelect
								className="w-full [&>button]:w-full [&>button]:!justify-between [&>button]:!font-normal"
								menuWidth="trigger"
								placeholder="Select country"
								searchPlaceholder="Search country"
								value={userInfoState.country || undefined}
								options={countriesList.map((c) => ({ value: c, label: c }))}
								onValueChange={(v) => handleChange(v, 'country')}
							/>
							{userInfoErrors.country && (
								<p className="text-[12px] leading-4 text-[color:var(--error-ink,#f04438)]">
									{userInfoErrors.country}
								</p>
							)}
						</div>
						<div className="flex w-full flex-col gap-1.5">
							<p className="text-[14px] leading-[18px] font-medium text-[color:var(--text-primary)]">
								Timezone
							</p>
							<SearchableSelect
								className="w-full [&>button]:w-full [&>button]:!justify-between [&>button]:!font-normal"
								menuWidth="trigger"
								placeholder="Select timezone"
								searchPlaceholder="Search timezone"
								value={userInfoState.timeZone || undefined}
								options={tzList.map((t) => ({ value: t, label: t }))}
								onValueChange={(v) => handleChange(v, 'timeZone')}
							/>
							{userInfoErrors.timeZone && (
								<p className="text-[12px] leading-4 text-[color:var(--error-ink,#f04438)]">
									{userInfoErrors.timeZone}
								</p>
							)}
						</div>
					</div>
				</StatCard>

				<StatCard
					size="regular"
					divider={false}
					className="w-full !p-4 !gap-4"
					title={
						<span className="text-[18px] leading-6 font-medium text-t-base-blue">
							Email addresses
						</span>
					}
					trailing={
						<AppButton
							variant="primary"
							size="md"
							label="Add email"
							onClick={() => setNewEmail(true)}
						/>
					}
				>
					{newEmail && (
						<div className="flex items-end gap-3">
							<div className="flex-1">
								<Input
									size="md"
									state="default"
									placeholder="name@domain.com"
									value={emailValue}
									onChange={(e) => setEmailValue(e.target.value)}
								/>
							</div>
							<AppButton
								variant="secondary"
								size="md"
								label="Cancel"
								onClick={() => {
									setNewEmail(false)
									setEmailValue('')
								}}
							/>
							<AppButton
								loader="dots"
								variant="primary"
								size="md"
								label="Add Email"
								isLoading={isLoading('USER_ADD_NEW_EMAIL')}
								onClick={handleAddnewEmail}
							/>
						</div>
					)}
					<div className="flex flex-col">
						{user.email_addresses.map((email, idx) => (
							<EmailItem key={idx} email={email} />
						))}
					</div>
				</StatCard>
			</div>
		</div>
	)
}

const EmailItem = ({ email }: { email: EmailAddresInfoDTO }) => {
	const { userStore } = useStores()
	const isLoading = useUiStore((s) => s.isLoading)
	const { resendVerificationEmail, deleteEmail } = userStore
	const [openVerificationConfirmation, setOpenVerificationConfirmation] =
		useState(false)
	const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)

	const handleVerificationEmail = async () => {
		try {
			await resendVerificationEmail({ email: email.email })
		} catch (error) {
			return
		}
	}

	const handleDeleteEmail = async () => {
		try {
			await deleteEmail({ email: email.email })
		} catch (error) {
			return
		}
	}

	return (
		<div className="flex min-h-[72px] items-center justify-between gap-4">
			<p className="min-w-0 flex-1 truncate text-[14px] leading-[18px] text-[color:var(--text-primary)]">
				{email.email}
			</p>
			<div className="flex shrink-0 items-center gap-4">
				<Badge
					className={`${BADGE_BASE} ${
						email.email_verified ? VERIFIED_BADGE : UNVERIFIED_BADGE
					}`}
				>
					{email.email_verified ? 'Verified' : 'Not verified'}
				</Badge>
				{!email.email_verified && (
					<Tooltip content="Resend confirmation email">
						<AppButton
							variant="secondary"
							size="md"
							label="Resend"
							leftIcon={<RefreshCw01 className="h-4 w-4" />}
							className="!border-t-card-border !text-t-base-blue"
							onClick={() => setOpenVerificationConfirmation(true)}
						/>
					</Tooltip>
				)}
				<Tooltip content="Remove email address">
					<AppButton
						variant="secondary"
						size="md"
						label="Remove"
						leftIcon={<Download01 className="h-4 w-4" />}
						className="!border-t-card-border !text-t-base-blue"
						onClick={() => setOpenDeleteConfirmation(true)}
					/>
				</Tooltip>
			</div>

			<ConfirmDialog
				open={openVerificationConfirmation}
				onClose={() => setOpenVerificationConfirmation(false)}
				onConfirm={handleVerificationEmail}
				title="Resend verification code"
				description="You’re about to send another verification for this email. Check your inbox for a verification link. Proceed?"
				confirmLabel="Resend"
				loading={isLoading('USER_RESEND_VERIFICATION_EMAIL')}
			/>
			<ConfirmDialog
				open={openDeleteConfirmation}
				onClose={() => setOpenDeleteConfirmation(false)}
				onConfirm={handleDeleteEmail}
				tone="danger"
				title="Remove email address"
				description="This action will delete this email address from your profile. You can’t remove the email if it’s used for login method or notifications. Proceed?"
				confirmLabel="Remove"
				loading={isLoading('USER_DELETE_EMAIL')}
			/>
		</div>
	)
}
